import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * The grid is a window over the catalogue. Pages the shopper has scrolled past
 * are collapsed into a placeholder that keeps EXACTLY the space they took, so
 * nothing above the viewport ever changes height while they scroll — in any
 * browser, whether or not it anchors the scroll position for us. Pages come
 * back out of their placeholder as the shopper returns to them, in the same
 * rows at the same heights. The only time content is inserted above the
 * viewport is a page never seen in this session (a deep link or a return from
 * a product page, which on this store is always a full reload), and that page
 * is laid out for real before the viewport is put back, once, while the
 * shopper's hand is off the wheel.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * Maximum number of pages of cards kept rendered at once. Infinite scroll
   * appends/prepends pages indefinitely; without a cap the node, image and SVG
   * count grows unbounded and eventually saturates CPU/GPU memory. Cached page
   * HTML stays in `this.pages`, so collapsed pages re-render instantly on
   * scroll-back.
   * @type {number}
   */
  static maxPagesInDom = 5;

  /**
   * Deadline (ms) for the idle callbacks that prefetch the next/previous page.
   *
   * These MUST have a timeout. A collection grid streams images and runs a
   * layout task every time a page is appended, so the browser gets little idle
   * time: measured `requestIdleCallback` scheduling latency on /collections/all
   * was p50 2.6s, p75 7.5s, max 10s. Without a deadline the prefetch loses the
   * race to the scroll sentinel every time, and each page ends up fetched on
   * demand once the shopper is already at the bottom.
   * @type {number}
   */
  static prefetchTimeout = 300;

  /**
   * How far ahead of the viewport a page is rendered, as the sentinel and
   * placeholder observers' root margin. Two full viewports: appending a page
   * is a sizeable task, so at flick speed one viewport was not enough of a
   * runway and the shopper reached the bottom before the cards landed.
   * @type {string}
   */
  static renderAhead = '200% 0px';

  /**
   * Class of the empty grid cells this list inserts to keep every card in the
   * column it already occupies when pages are added above or taken away.
   *
   * A page is rarely a whole number of grid rows (/collections/all renders 22
   * or 23 cards a page across 2, 3 or 4 columns), and the grid auto-places
   * cards in DOM order. So prepending a page, or collapsing one, shifts every
   * later card by `count mod columns` columns: rows re-pair, the cards on
   * screen swap places under the shopper, and every re-paired row changes
   * height (a row is as tall as its tallest card). Padding each such change out
   * to a multiple of the column count with spacer cells keeps the sequence
   * below it fixed. Spacers are not `ref="cards[]"`, so the cursor, visibility
   * and page logic never see them. Styled in assets/base.css.
   * @type {string}
   */
  static spacerClass = 'product-grid__item--spacer';

  /**
   * Class of the cell that stands in for a collapsed page: one full-width grid
   * item as tall as the page's whole rows were. Styled in assets/base.css.
   * @type {string}
   */
  static placeholderClass = 'product-grid__item--placeholder';

  /**
   * Sorts the storefront cannot do, keyed by their `sort_by` value. Shopify's
   * `sort_by` cannot order by a metafield, so for these the list loads every
   * page of the collection once, orders the cards itself by the card attribute
   * named here, and then pages through the ordered set exactly as it would
   * through the server's pages. Selecting one navigates to it — see
   * assets/collection-trust-sort.js, which also keeps the sort control in sync.
   * @type {Record<string, { attribute: string, descending: boolean, label: string }>}
   */
  static clientSorts = {
    'trust-score': { attribute: 'data-trust-score', descending: true, label: 'TrustScore' },
  };

  /**
   * How many pages a client sort fetches at once. The storefront throttles
   * bursts of section renders (a 430 comes back as a body with no grid), so
   * this stays low; the pages are cached by section-renderer either way.
   * @type {number}
   */
  static clientSortConcurrency = 3;

  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {IntersectionObserver | undefined} */
  #cardVisibilityObserver;

  /** @type {IntersectionObserver | undefined} */
  #placeholderObserver;

  /** @type {Set<Element>} */
  #visibleCards = new Set();

  /**
   * Pages that rendered no cards at all. The grid skips products the shopper
   * must not see (sold out, swap alternates, non-main combined-listing
   * children), and a long enough run of them empties a whole page — on
   * /collections/all, in the order the storefront sorts it, page 36 of 57
   * renders zero cards because 35 children of one product line sit together.
   *
   * Such a page has to be stepped over rather than rendered. `#getPage` reads
   * the cursor off the pages in the DOM, so a page that inserts nothing leaves
   * the cursor where it was; and since the sentinel is already intersecting,
   * the IntersectionObserver never fires again. Without this set the catalogue
   * silently ends at the first empty page.
   * @type {Set<number>}
   */
  #emptyPages = new Set();

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /**
   * Render locks per direction: the background prepend and the sentinel
   * observer can both ask for the same page — only one may insert it.
   */
  #renderingNext = false;

  #renderingPrevious = false;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /**
   * Row height of every card collapsed out of the DOM, by card id. A collapsed
   * page comes back as freshly parsed elements, which the browser has never
   * rendered, so it would size them from the grid-wide estimate; handing each
   * one its own measured height instead means it re-enters the grid at
   * exactly the size its placeholder reserved.
   * @type {Map<string, number>}
   */
  #cardHeights = new Map();

  /**
   * Trailing parity spacers each collapsed page carried (page 1's, see
   * #spacersToPad), recreated when the page is restored.
   * @type {Map<number, number>}
   */
  #trailingSpacers = new Map();

  /**
   * The card elements of each collapsed page, detached. A page is restored
   * from clones of these — fresh elements, connected once like any other
   * card, but without a parse, and sharing the images the originals already
   * fetched and decoded, so a page scrolled back to comes up whole instead of
   * popping in. They are also the only record of the page the server
   * rendered into the document, which never passes through `pages`. Bounded
   * by {@link PaginatedList.maxCollapsedPagesKept}; pages beyond it fall back
   * to their HTML.
   * @type {Map<number, Element[]>}
   */
  #collapsedCards = new Map();

  /**
   * How many collapsed pages keep their live nodes around (see
   * #collapsedCards) — the nearest ones, which are the ones a shopper scrolls
   * back to. Farther pages are serialised to HTML, which costs nothing to hold.
   * @type {number}
   */
  static maxCollapsedPagesKept = 8;

  /**
   * Whether `--product-card-intrinsic-height` has been measured for the grid's
   * current shape. Reset whenever the column width or the view changes.
   */
  #estimateMeasured = false;

  /** @type {ResizeObserver | undefined} */
  #gridResizeObserver;

  /** @type {MutationObserver | undefined} */
  #gridViewObserver;

  /** Inline size of the grid at the last estimate, to tell resizes from height changes. */
  #gridInlineSize = 0;

  /**
   * On-screen cards that reported themselves rendered at the previous
   * measurement attempt. A card is only trusted to have its real height once
   * it has reported rendered on two consecutive attempts — see
   * #measureCardEstimate.
   * @type {WeakSet<Element>}
   */
  #renderedAtLastAttempt = new WeakSet();

  /** The grid the estimate observers are attached to. */
  #observedGrid = null;

  /** Column count the spacers currently in the grid were computed for. */
  #columns = 0;

  /** Time of the last scroll input, for #whenScrollIdle. */
  #lastScrollInput = -Infinity;

  /**
   * Bumped whenever the grid's contents are replaced wholesale (a filter
   * change, a client sort landing). A render that was waiting on a fetch when
   * that happened must not insert its page into the new grid.
   */
  #epoch = 0;

  /** Whether a filter change has been announced and its new grid not yet arrived. */
  #filtering = false;

  /**
   * The active client sort, if the URL asks for one: its key, and once
   * loaded, the ordered cards cut into pages.
   * @type {{ key: string, pages: Map<number, Element[]> | null, generation: number } | null}
   */
  #clientSort = null;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    for (const type of ['wheel', 'touchmove', 'scroll']) {
      window.addEventListener(type, this.#handleScrollInput, { passive: true });
    }

    // Point the history entry at the card being opened, so returning from the
    // PDP restores this spot even without the back/forward cache.
    this.addEventListener('click', this.#handleCardNavigation);

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    this.#observeCardEstimate();

    const sortKey = PaginatedList.#clientSortKey();
    if (sortKey) {
      this.#startClientSort(sortKey);
      return;
    }

    this.#start();
  }

  /**
   * Wires the infinite scroll up to the cards currently in the grid — on
   * connect, and again after a filter change or a client sort has replaced
   * them.
   */
  #start() {
    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();
    this.#observeCardVisibility();
    this.#observePlaceholders();

    const url = new URL(window.location.href);
    const initialPage = Number(url.searchParams.get('page') ?? 1);

    // The landing page itself can be one of the empty ones — its paginated URL
    // is shareable and crawlable, so shoppers and bots do arrive on it. It
    // renders a grid with no cards, which leaves `#getPage` no cursor to read;
    // record it so the fallback below starts from the URL and the sentinels
    // load the nearest pages that do have cards, instead of a blank grid that
    // never fills.
    const { grid: initialGrid } = this.refs;
    const initialCards = this.#cards();
    if (initialGrid && !initialCards.length && Number.isFinite(initialPage)) {
      this.#emptyPages.add(initialPage);
    }

    // A deep link lands the viewport somewhere inside the page, with rows of
    // this same page above it. Off-screen rows are sized by the estimate until
    // they render, and the first scroll up would render them at their real
    // height — under the shopper, uncompensated in browsers without scroll
    // anchoring. Lay the whole landing page out for real once, now, so every
    // row above the hash card has its true height before anyone scrolls.
    if (initialCards.length && (initialPage > 1 || url.hash)) {
      this.#forceRender(initialCards);
    }

    // Deep links land mid-catalog (?page=N). Prepend the previous page in the
    // background so the first upward scroll already has content above it,
    // instead of waiting for the top sentinel to fire mid-scroll.
    if (initialPage > 1) {
      requestIdleCallback(() => this.#renderPreviousPage(), { timeout: PaginatedList.prefetchTimeout });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    this.#cardVisibilityObserver?.disconnect();
    this.#placeholderObserver?.disconnect();
    this.#gridResizeObserver?.disconnect();
    this.#gridViewObserver?.disconnect();
    this.#observedGrid?.removeEventListener('contentvisibilityautostatechange', this.#handleCardStateChange);
    this.#observedGrid = null;
    this.removeEventListener('click', this.#handleCardNavigation);
    for (const type of ['wheel', 'touchmove', 'scroll']) {
      window.removeEventListener(type, this.#handleScrollInput);
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #handleScrollInput = () => {
    this.#lastScrollInput = performance.now();
  };

  /**
   * Runs `callback` at the first moment the shopper is not scrolling: at once
   * if nothing has moved for 120ms, otherwise as soon as it has. Content
   * inserted above the viewport has to be paid for with a scroll adjustment,
   * and an adjustment landing in the middle of a wheel or momentum scroll is
   * felt as the page jumping (iOS ends the fling on it). Waits at most a
   * second and a half so a shopper who never stops still gets their page.
   *
   * @param {() => void} callback
   */
  #whenScrollIdle(callback) {
    const quiet = 120;
    const deadline = performance.now() + 1500;
    const check = () => {
      if (!this.isConnected) return;
      const now = performance.now();
      if (now - this.#lastScrollInput >= quiet || now >= deadline) {
        callback();
        return;
      }
      setTimeout(check, quiet - (now - this.#lastScrollInput));
    };
    check();
  }

  /**
   * Lays `cards` out for real even while they are off-screen, so a row above
   * the viewport has its true height from the start and never changes size
   * when it finally scrolls into view. Layout itself is cheap (22ms for a
   * page of 24 cards measured 2026-09-21); what costs is the DOM insertion,
   * which happens either way.
   *
   * They stay laid out. Handing a card back to `content-visibility: auto`
   * makes the browser treat it as off-screen until the next frame's
   * intersection check, so a card in view goes blank for a frame — measured
   * as a `skipped` state change on every visible card. At most a window's
   * worth of pages (#trimRenderedPages) is ever held this way, and a page
   * collapsed out of the window takes the override with it.
   *
   * @param {Iterable<Element>} cards
   */
  #forceRender(cards) {
    for (const card of cards) {
      if (card instanceof HTMLElement) card.style.contentVisibility = 'visible';
    }
  }

  /**
   * Watches which cards are on screen so the address bar always points at the
   * page the user is actually viewing (the look-ahead prefetch runs a page
   * beyond it). That makes the URL shareable at any scroll position: whoever
   * opens it lands on the same products. Safe to call repeatedly — observing
   * an already-observed element is a no-op.
   */
  #observeCardVisibility() {
    if (!this.#cardVisibilityObserver) {
      this.#cardVisibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#visibleCards.add(entry.target);
          } else {
            this.#visibleCards.delete(entry.target);
          }
        }
        this.#syncUrlToViewedPage();
      });
    }

    // From the DOM, not `refs`: refs re-sync in a microtask after a mutation,
    // so right after an append they still describe the previous page.
    for (const card of this.#cards()) {
      this.#cardVisibilityObserver.observe(card);
    }
  }

  /**
   * Rewrites the current history entry (never adds one) so its `page` param
   * matches the topmost page in view. Only touches history when the viewed
   * page changes, which also keeps us clear of Safari's replaceState limits.
   */
  #syncUrlToViewedPage() {
    let viewedPage = Infinity;
    for (const card of this.#visibleCards) {
      if (!(card instanceof HTMLElement)) continue;
      const page = Number(card.dataset.page);
      if (Number.isFinite(page) && page > 0 && page < viewedPage) viewedPage = page;
    }
    if (viewedPage === Infinity) return;

    const url = new URL(window.location.href);
    const currentPage = Number(url.searchParams.get('page') ?? 1);

    if (viewedPage === currentPage) return;

    if (viewedPage === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', viewedPage.toString());
    }

    // Any hash pointed at a card on the page being scrolled away from.
    url.hash = '';

    history.replaceState(history.state, '', url.toString());
  }

  /**
   * Keeps the grid's `content-visibility` estimate tied to a card that has
   * actually rendered.
   *
   * `results-list .product-grid__item` (assets/base.css) skips layout for
   * off-screen cards and stands each one in with
   * `--product-card-intrinsic-height`. The stylesheet can only guess that per
   * breakpoint, but a card's height follows its column width — the desktop grid
   * is `auto-fill`, so 1440px gets 329px columns and 1920px gets 258px — and
   * every skipped row mis-sized by the gap between guess and reality is a jolt
   * the shopper feels when it renders. So once a row is on screen, its height
   * becomes the estimate for the whole grid, and it is measured again whenever
   * the columns change width or the shopper switches view.
   */
  #observeCardEstimate() {
    const { grid } = this.refs;
    if (!grid || grid === this.#observedGrid) return;

    this.#gridResizeObserver?.disconnect();
    this.#gridViewObserver?.disconnect();
    this.#observedGrid?.removeEventListener('contentvisibilityautostatechange', this.#handleCardStateChange);
    this.#observedGrid = grid;
    this.#gridInlineSize = 0;
    this.#columns = this.#columnCount();

    // Fires on the card as it enters or leaves the rendered state — the moment
    // there is a real height to read. Bubbles, so one listener covers the grid.
    grid.addEventListener('contentvisibilityautostatechange', this.#handleCardStateChange);

    // The grid grows with every appended page, so only an INLINE size change
    // means the columns (and with them every card) changed shape.
    this.#gridResizeObserver = new ResizeObserver((entries) => {
      const inlineSize = entries[0]?.contentBoxSize?.[0]?.inlineSize ?? grid.clientWidth;
      if (inlineSize === this.#gridInlineSize) return;
      this.#gridInlineSize = inlineSize;
      this.#scheduleEstimate();
    });
    this.#gridResizeObserver.observe(grid);

    // View switches (mobile-single, zoom-out) re-lay every card without
    // touching the grid's width.
    this.#gridViewObserver = new MutationObserver(() => this.#scheduleEstimate());
    this.#gridViewObserver.observe(grid, { attributes: true, attributeFilter: ['product-grid-view'] });

    // Cards already on screen may have rendered before this listener existed
    // (the grid is at the top of a deep-linked page, say), in which case no
    // state change is coming for them.
    this.#scheduleEstimate();

    // The first rows can render in the fallback font, and the web font that
    // replaces it a moment later sets the title and brand lines tighter —
    // measured 522px against a settled 485px on the desktop grid. Measure
    // again once the fonts are in; the correction holds the viewport in place.
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#scheduleEstimate();
    });
  }

  /**
   * @param {Event} event
   */
  #handleCardStateChange = (event) => {
    if (!(event.target instanceof HTMLElement) || /** @type {any} */ (event).skipped) return;

    // A restored card carried its old height as an inline reservation (see
    // #collapsePage). Now that it has rendered the browser remembers the real
    // size itself, so drop the override — it would otherwise keep sizing the
    // card after a view change made the old number wrong.
    event.target.style.removeProperty('contain-intrinsic-block-size');

    // The card is laid out for real from the next frame; give it two.
    if (!this.#estimateMeasured) this.#attemptEstimate(2);
  };

  #scheduleEstimate() {
    this.#estimateMeasured = false;
    this.#renderedAtLastAttempt = new WeakSet();
    this.#syncToColumns();

    // A card that has just come on screen is not laid out until a frame or
    // two later (`content-visibility` decides what to render from the
    // previous frame's intersections), and there is no event for a card that
    // rendered before this list was listening. So keep trying for a short
    // while, once per frame, after each frame has rendered; a grid still below
    // the fold after that is picked up by #handleCardStateChange on the way in.
    this.#attemptEstimate(30);
  }

  /**
   * Measures once per frame, after the frame has rendered, until it succeeds
   * or `framesLeft` runs out.
   *
   * @param {number} framesLeft
   */
  #attemptEstimate(framesLeft) {
    requestAnimationFrame(() =>
      setTimeout(() => {
        if (this.#estimateMeasured || !this.isConnected) return;
        this.#measureCardEstimate();
        if (!this.#estimateMeasured && framesLeft > 0) this.#attemptEstimate(framesLeft - 1);
      }, 0)
    );
  }

  /**
   * Reads the height of every row that has a real size — rendered now, or
   * rendered once and remembered by the browser since — and writes their
   * median to the grid as `--product-card-intrinsic-height`. Rows come in
   * clusters (a variant strip under the image or not, a wrapped title or not),
   * so the median lands the estimate on the typical row rather than an
   * outlier, and the wider the sample the closer it lands: at first paint only
   * the rows on screen count, by the fonts-ready re-measure the rows within
   * the render margin have joined them.
   *
   * Changing the estimate resizes every skipped row that has never rendered.
   * Those rows are all below the viewport — every row above it has either
   * rendered or been laid out for real on insertion (#forceRender) — so the
   * change moves nothing the shopper can see; the anchor check is kept as a
   * guard, in document coordinates and applied as an absolute scroll, so it is
   * a no-op when there was no shift or the browser's own anchoring already
   * absorbed it.
   */
  #measureCardEstimate() {
    const { grid } = this.refs;
    if (!grid || this.#estimateMeasured || !grid.isConnected) return;

    const current = parseFloat(getComputedStyle(grid).getPropertyValue('--product-card-intrinsic-height'));

    /** @type {Map<number, number>} row top -> row height, rows with a real size only */
    const rows = new Map();
    let onScreen = false;
    const renderedNow = new WeakSet();

    for (const card of this.#cards()) {
      const { top, bottom, height } = card.getBoundingClientRect();
      if (height === 0) continue;
      const visible = bottom > 0 && top < window.innerHeight;
      const atEstimate = Math.abs(height - current) < 0.5;

      if (visible) {
        // On screen. Rendered cards have their real height — except in the
        // frame a card is first deemed relevant, when `checkVisibility` already
        // answers true while the box is still the reserved one. So a card at
        // exactly the estimate is trusted only once it has reported rendered
        // on two consecutive attempts; by then it is laid out, and a real row
        // that happens to match the estimate is simply a row the estimate got
        // right.
        if (!PaginatedList.#isRendered(card)) continue;
        renderedNow.add(card);
        if (atEstimate && !this.#renderedAtLastAttempt.has(card)) continue;
        onScreen = true;
      } else if (atEstimate) {
        // Off screen and standing at the estimate: skipped, nothing to read.
        // Off screen at any other height is a size the browser remembered
        // from an earlier render, which is as real as it gets.
        continue;
      }

      rows.set(Math.round(top), height);
    }
    this.#renderedAtLastAttempt = renderedNow;

    // Nothing measurable, or nothing measurable in view — a remembered row far
    // above says little about the layout the shopper is looking at.
    if (!rows.size || !onScreen) return;

    const heights = [...rows.values()].sort((a, b) => a - b);
    const estimate = Math.round(heights[Math.floor((heights.length - 1) / 2)] ?? 0);
    if (!estimate) return;

    this.#estimateMeasured = true;
    if (Math.abs(current - estimate) < 1) return;

    const anchor = this.#viewportAnchor();
    const scrollTop = window.scrollY;
    const before = anchor ? anchor.getBoundingClientRect().top + scrollTop : 0;
    grid.style.setProperty('--product-card-intrinsic-height', `${estimate}px`);
    if (!anchor) return;
    const after = anchor.getBoundingClientRect().top + window.scrollY;

    const target = scrollTop + (after - before);
    if (Math.abs(window.scrollY - target) > 1) {
      window.scrollTo({ top: target, behavior: 'instant' });
    }
  }

  /**
   * Whether a `content-visibility: auto` card's contents are currently laid
   * out. The check has to be made on a descendant: the card itself always
   * reports visible, since it is its CONTENTS that are skipped. Browsers
   * without the option are taken at their word.
   *
   * @param {HTMLElement} card
   * @returns {boolean}
   */
  static #isRendered(card) {
    // The item opens with a <style> (the card's scoped styles), which is never
    // "visible" — find the first child that is actually laid out.
    const content = [...card.children].find((child) => !['STYLE', 'SCRIPT', 'TEMPLATE'].includes(child.tagName));
    if (!(content instanceof HTMLElement) || typeof content.checkVisibility !== 'function') return true;
    return content.checkVisibility({ contentVisibilityAuto: true });
  }

  /**
   * Gives freshly parsed cards the height they had the last time they were in
   * the DOM, so a collapsed page re-enters the grid at its real size instead
   * of the grid-wide estimate. Cards never seen before are left to the
   * estimate.
   *
   * @param {Iterable<Element>} cards
   */
  #applyRememberedHeights(cards) {
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const height = this.#cardHeights.get(card.id);
      if (height) card.style.containIntrinsicBlockSize = `auto ${height}px`;
    }
  }

  #handleCardNavigation = (event) => {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('a[href]')?.closest('[ref="cards[]"]');
    if (!(card instanceof HTMLElement)) return;

    const url = new URL(window.location.href);
    const { page } = card.dataset;

    if (page === '1') {
      url.searchParams.delete('page');
    } else if (page) {
      url.searchParams.set('page', page);
    }

    if (card.id) url.hash = card.id;

    history.replaceState(history.state, '', url.toString());
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: PaginatedList.renderAhead,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * Brings a collapsed page back as the shopper approaches its placeholder.
   * The placeholders sit between the top sentinel and the rendered pages, so
   * they are always reached — and restored — before the sentinel is.
   */
  #observePlaceholders() {
    if (!this.#placeholderObserver) {
      this.#placeholderObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) await viewTransition.current;

          // Nearest to the rendered window first, so a long jump (dragging the
          // scrollbar) restores pages in order and trims from the right end.
          const due = entries
            .filter((entry) => entry.isIntersecting && entry.target instanceof HTMLElement)
            .map((entry) => /** @type {HTMLElement} */ (entry.target))
            .sort((a, b) => Math.abs(a.getBoundingClientRect().top) - Math.abs(b.getBoundingClientRect().top));

          for (const placeholder of due) {
            const page = Number(placeholder.dataset.placeholderPage);
            if (!placeholder.isConnected || !Number.isFinite(page)) continue;
            this.#restorePage(page);
          }
        },
        { rootMargin: PaginatedList.renderAhead }
      );
    }

    for (const placeholder of this.#placeholders()) {
      this.#placeholderObserver.observe(placeholder);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   * @param {boolean} [useCache] - Whether section-renderer may answer from its
   *   own cache. `false` re-fetches a page whose cached body had no grid.
   */
  async #fetchSpecificPage(pageNumber, url = undefined, useCache = true) {
    // A client sort holds every page already.
    if (this.#clientSort) return;

    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, useCache, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Resolves the nearest page in `direction` that actually has cards to insert,
   * fetching it if it is not cached yet. Pages that render none are recorded in
   * {@link #emptyPages} and stepped over rather than returned — inserting
   * nothing would leave the cursor and the sentinel exactly where they are, and
   * the list would stop there for good.
   *
   * @param {'next' | 'previous'} direction
   * @returns {Promise<{ page: number, elements: Element[] } | null>}
   */
  async #resolveNonEmptyPage(direction) {
    /** Pages already re-fetched once in this call — see the `!elements` branch. */
    const retried = new Set();

    while (true) {
      const pageInfo = this.#getPage(direction);

      if (!pageInfo || !this.#shouldUsePage(pageInfo) || this.#isPageInDom(pageInfo.page)) return null;

      let elements = this.#getGridForPage(pageInfo.page);

      if (!elements) {
        const promise = new Promise((res) => {
          if (direction === 'next') {
            this.#resolveNextPagePromise = res;
          } else {
            this.#resolvePreviousPagePromise = res;
          }
        });

        // Trigger the fetch for this page
        this.#fetchPage(direction);

        await promise;
        elements = this.#getGridForPage(pageInfo.page);
      }

      if (!elements) {
        // Cached HTML that carries no grid is a failed fetch, not an empty
        // page: `getSectionHTML` never checks `response.ok`, so a throttled
        // (430) or errored body lands in `pages` looking exactly like content.
        // Recording it in #emptyPages would drop that page's products silently
        // for the rest of the session, and section-renderer caches the bad body
        // by URL so nothing would ever re-fetch it. Purge both caches and retry
        // once, then give up rather than guess. (The section's own empty state
        // also omits the grid, but it keys off the unfiltered page slice, which
        // is never empty for a page within `data-last-page`.)
        if (this.pages.has(pageInfo.page) && !retried.has(pageInfo.page)) {
          retried.add(pageInfo.page);
          this.pages.delete(pageInfo.page);
          try {
            await this.#fetchSpecificPage(pageInfo.page, pageInfo.url, false);
          } catch {
            return null;
          }
          continue;
        }
        return null;
      }

      // Re-check after the await: someone else may have inserted this page
      if (this.#isPageInDom(pageInfo.page)) return null;

      if (elements.length === 0) {
        this.#emptyPages.add(pageInfo.page);
        continue;
      }

      return { page: pageInfo.page, elements };
    }
  }

  /** Whether a client sort is still loading its pages — nothing may render meanwhile. */
  get #sortLoading() {
    return Boolean(this.#clientSort && !this.#clientSort.pages);
  }

  /** Whether nothing may be inserted into the grid right now. */
  get #frozen() {
    return this.#sortLoading || this.#filtering;
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid || this.#renderingNext || this.#frozen) return;

    this.#renderingNext = true;
    const epoch = this.#epoch;

    try {
      const resolved = await this.#resolveNonEmptyPage('next');
      if (!resolved || !grid.isConnected || epoch !== this.#epoch || this.#frozen) return;

      // Appending below the viewport moves nothing the shopper can see.
      this.#applyRememberedHeights(resolved.elements);
      grid.append(...resolved.elements);

      this.#aspectRatioHelper?.processNewElements();

      this.#trimRenderedPages('next');

      // The URL is updated by #syncUrlToViewedPage when the new cards actually
      // scroll into view — appending prefetched content must not touch history.
      this.#observeCardVisibility();
    } finally {
      this.#renderingNext = false;
    }

    requestIdleCallback(
      () => {
        this.#fetchPage('next');
      },
      { timeout: PaginatedList.prefetchTimeout }
    );
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid || this.#renderingPrevious || this.#frozen) return;

    this.#renderingPrevious = true;
    const epoch = this.#epoch;

    try {
      const resolved = await this.#resolveNonEmptyPage('previous');
      if (!resolved || !grid.isConnected || epoch !== this.#epoch || this.#frozen) return;

      await new Promise((resolve) => this.#whenScrollIdle(resolve));
      if (!grid.isConnected || epoch !== this.#epoch || this.#frozen || this.#isPageInDom(resolved.page)) return;

      const previousPageItemElements = resolved.elements;

      // Store the current scroll position and the document position of the
      // card the shopper is looking at (see #viewportAnchor for why it has to
      // be that one and not the grid's first card).
      const scrollTop = window.scrollY;
      const firstElement = this.#viewportAnchor();
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // This page has never been rendered in this session (a page that has
      // comes back through #restorePage, which moves nothing), so lay it out
      // for real as it goes in: the compensation below is then exact in one
      // step, and nothing above the viewport is left to settle later.
      this.#forceRender(previousPageItemElements);

      // Prepend the new elements. Any spacers standing in at the top for
      // parity give way to the page itself; the count going in is padded so
      // that what follows keeps its columns — see #spacersToPad.
      const topSpacers = this.#spacers('top');
      const padding = this.#spacersToPad(previousPageItemElements.length, topSpacers.length, resolved.page);
      for (const spacer of topSpacers) spacer.remove();
      grid.prepend(...(padding[0]?.dataset.spacer === 'top' ? [...padding, ...previousPageItemElements] : [...previousPageItemElements, ...padding]));

      this.#aspectRatioHelper?.processNewElements();

      // See #renderNextPage — the URL follows the viewed page, not the fetch.
      this.#observeCardVisibility();

      // Put the viewport back where it was, in DOCUMENT coordinates and as an
      // absolute scroll position. Chrome and Firefox anchor the viewport to the
      // card on their own when content is inserted above it (Safari does not),
      // and that adjustment has already happened by the time the layout below
      // is read — a relative `scrollBy` would then correct the same shift
      // twice. An absolute target is a no-op when the browser got there first.
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const restored = scrollTop + (newHeight - oldHeight);
        if (Math.abs(window.scrollY - restored) > 1) {
          window.scrollTo({ top: restored, behavior: 'instant' });
        }
      }

      this.#trimRenderedPages('previous');
    } finally {
      this.#renderingPrevious = false;
    }

    requestIdleCallback(
      () => {
        this.#fetchPage('previous');
      },
      { timeout: PaginatedList.prefetchTimeout }
    );
  }

  /**
   * Enforces the {@link PaginatedList.maxPagesInDom} sliding window by
   * collapsing rendered pages at the end opposite the direction the list just
   * grew in. A collapsed page keeps its exact space (#collapsePage), so this
   * never moves the viewport; its HTML stays cached, so it re-renders
   * instantly on scroll-back.
   *
   * @param {"next" | "previous"} direction - Which end was just extended.
   */
  #trimRenderedPages(direction) {
    const rendered = this.#pagesInDom().filter((entry) => entry.rendered);
    if (rendered.length <= PaginatedList.maxPagesInDom) return;

    const excess = rendered.length - PaginatedList.maxPagesInDom;
    const dropping =
      direction === 'next'
        ? rendered.slice(0, excess) // collapse oldest (top)
        : rendered.slice(rendered.length - excess); // collapse newest (bottom)

    for (const { page } of dropping) this.#collapsePage(page);
  }

  /**
   * Swaps a rendered page for a placeholder that takes exactly the same space,
   * so the cards around it — and everything below — stay where they are.
   *
   * A page's cards fall into rows of the grid. Its whole rows become one
   * full-width cell as tall as they were together. A row it shares with the
   * page before or after it (a page is rarely a whole number of rows) keeps
   * the other page's cards, and the page's own cells in it become spacers of
   * the row's height, so the row neither re-pairs nor shrinks. Cell parity is
   * preserved by construction: shared-row cards are replaced one for one, and
   * whole rows contribute a multiple of the column count.
   *
   * Rows are read from the layout rather than counted, so the same code holds
   * for any column count, for the parity spacers already in the grid, and for
   * a page that is nothing but a shared row.
   *
   * @param {number} page
   */
  #collapsePage(page) {
    const { grid } = this.refs;
    if (!grid || !this.#isPageRendered(page) || this.#columnCount() < 1) return;

    /** @type {HTMLElement[]} */
    const cells = [];
    for (const child of grid.children) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.dataset.page === String(page) && (child.getAttribute('ref') === 'cards[]' || child.dataset.spacer)) {
        cells.push(child);
      }
    }
    if (!cells.length) return;

    // Rows: cells grouped by their top edge, in document order.
    /** @type {{ top: number, bottom: number, cells: HTMLElement[] }[]} */
    const rows = [];
    for (const cell of cells) {
      const rect = cell.getBoundingClientRect();
      const top = Math.round(rect.top);
      const row = rows.find((candidate) => Math.abs(candidate.top - top) < 2);
      if (row) {
        row.cells.push(cell);
        row.bottom = Math.max(row.bottom, rect.bottom);
      } else {
        rows.push({ top, bottom: rect.bottom, cells: [cell] });
      }
    }
    rows.sort((a, b) => a.top - b.top);

    const columns = this.#columnCount();
    const firstRow = rows[0];
    const lastRow = rows[rows.length - 1];
    // The page does not own its first row when a cell of something else (the
    // page before, or its spacers) sits in it — the previous grid cell is on
    // the same row. It does not own its last row when that row is short: the
    // rows in between are always full, so only the grid's last row can be,
    // and whether something follows it yet or not, the cells it will share
    // the row with must land in the same columns.
    const before = cells[0].previousElementSibling;
    const firstShared =
      before instanceof HTMLElement && before.tagName === 'LI' && Math.abs(before.getBoundingClientRect().top - firstRow.top) < 2;
    const lastShort = lastRow.cells.length < columns;
    const pinned = new Set();
    if (firstShared) pinned.add(firstRow);
    if (lastShort) pinned.add(lastRow);
    const wholeRows = rows.filter((row) => !pinned.has(row));

    let trailing = 0;
    for (const cell of cells) {
      if (cell.dataset.spacer) {
        trailing++;
        continue;
      }
      // Off-screen, so this reads the size the browser remembered from the
      // card's last render (or the estimate, if it never had one). Either way
      // it is the row height the page is currently laid out with.
      if (cell.id) this.#cardHeights.set(cell.id, cell.getBoundingClientRect().height);
      this.#cardVisibilityObserver?.unobserve(cell);
      this.#visibleCards.delete(cell);
    }
    this.#trailingSpacers.set(page, trailing);
    this.#keepCollapsed(
      page,
      cells.filter((cell) => !cell.dataset.spacer)
    );

    /** @type {HTMLElement[]} */
    const replacement = [];
    const pin = (row) => row.cells.map(() => this.#rowSpacer(page, row.bottom - row.top));
    if (pinned.has(firstRow)) replacement.push(...pin(firstRow));
    if (wholeRows.length) {
      // Bottom of the last whole row to top of the first: the row gaps
      // between them are inside that span, and the grid keeps the gaps on
      // either side of the placeholder's own row.
      replacement.push(this.#placeholder(page, wholeRows[wholeRows.length - 1].bottom - wholeRows[0].top));
    }
    if (pinned.has(lastRow) && lastRow !== firstRow) replacement.push(...pin(lastRow));
    // A page with no whole row of its own has no full-width cell; its first
    // spacer stands in as the one the observer watches and the page listing
    // reads. (A zero-height full-width cell would add a row gap of its own.)
    if (!wholeRows.length) replacement[0].dataset.placeholderMain = '';

    const anchor = cells[cells.length - 1].nextSibling;
    for (const cell of cells) cell.remove();
    grid.insertBefore(this.#fragment(replacement), anchor);

    this.#observePlaceholders();
  }

  /**
   * Puts a collapsed page back in place of its placeholder: the same cards,
   * each at the height its row had, so the rows re-form exactly and the
   * document does not change size around the viewport. Enforces the window
   * from the far end.
   *
   * @param {number} page
   */
  #restorePage(page) {
    const { grid } = this.refs;
    if (!grid || this.#isPageRendered(page) || this.#frozen) return;

    const block = this.#placeholderCells(page);
    if (!block.length) return;

    const kept = this.#collapsedCards.get(page);
    this.#collapsedCards.delete(page);
    // Clones, not the nodes themselves: a card's components register their
    // listeners on connect and not all of them let go on disconnect, so a
    // node connected twice would answer every event twice.
    const elements = kept ? kept.map((card) => /** @type {Element} */ (card.cloneNode(true))) : this.#getGridForPage(page);
    if (!elements) {
      // The cache was cleared under it (a filter change is on its way, and
      // the grid is about to be replaced). Drop the placeholder rather than
      // leave a hole that never fills.
      for (const cell of block) cell.remove();
      return;
    }

    this.#applyRememberedHeights(elements);
    const padding = Array.from({ length: this.#trailingSpacers.get(page) ?? 0 }, () => this.#trailingSpacer(page));
    this.#trailingSpacers.delete(page);

    const rendered = this.#pagesInDom().filter((entry) => entry.rendered);
    const above = rendered.length > 0 && page < rendered[0].page;

    const anchor = block[block.length - 1].nextSibling;
    for (const cell of block) {
      this.#placeholderObserver?.unobserve(cell);
      cell.remove();
    }
    grid.insertBefore(this.#fragment([...elements, ...padding]), anchor);

    this.#aspectRatioHelper?.processNewElements();
    this.#observeCardVisibility();

    // Restored above the window: the far end is the bottom, and vice versa.
    this.#trimRenderedPages(above ? 'previous' : 'next');
  }

  /**
   * Holds on to a collapsed page's nodes, letting the farthest page go once
   * more than {@link PaginatedList.maxCollapsedPagesKept} are held. A page
   * that is let go and has no cached HTML (the server-rendered one) is
   * serialised first, so it can always be restored.
   *
   * @param {number} page
   * @param {Element[]} cards
   */
  #keepCollapsed(page, cards) {
    this.#collapsedCards.set(page, cards);
    if (this.#collapsedCards.size <= PaginatedList.maxCollapsedPagesKept) return;

    const rendered = this.#pagesInDom().filter((entry) => entry.rendered);
    const centre = rendered.length ? (rendered[0].page + rendered[rendered.length - 1].page) / 2 : page;
    let farthest = page;
    for (const held of this.#collapsedCards.keys()) {
      if (Math.abs(held - centre) > Math.abs(farthest - centre)) farthest = held;
    }
    const letGo = this.#collapsedCards.get(farthest) ?? [];
    this.#collapsedCards.delete(farthest);
    if (!this.pages.has(farthest)) {
      this.pages.set(farthest, `<div ref="grid">${letGo.map((card) => card.outerHTML).join('')}</div>`);
    }
  }

  /**
   * @param {Node[]} nodes
   * @returns {DocumentFragment}
   */
  #fragment(nodes) {
    const fragment = document.createDocumentFragment();
    fragment.append(...nodes);
    return fragment;
  }

  /**
   * @param {number} page
   * @param {number} height
   * @returns {HTMLElement}
   */
  #placeholder(page, height) {
    const placeholder = document.createElement('li');
    placeholder.className = `product-grid__item ${PaginatedList.placeholderClass}`;
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.dataset.placeholderPage = String(page);
    placeholder.dataset.placeholderMain = '';
    placeholder.style.blockSize = `${height}px`;
    return placeholder;
  }

  /**
   * A spacer that keeps a shared row at the height it had.
   *
   * @param {number} page
   * @param {number} height
   * @returns {HTMLElement}
   */
  #rowSpacer(page, height) {
    const spacer = this.#trailingSpacer(page);
    spacer.dataset.spacer = 'row';
    spacer.dataset.placeholderPage = String(page);
    spacer.style.minBlockSize = `${height}px`;
    return spacer;
  }

  /**
   * @param {number} page
   * @returns {HTMLElement}
   */
  #trailingSpacer(page) {
    const spacer = document.createElement('li');
    spacer.className = `product-grid__item ${PaginatedList.spacerClass}`;
    spacer.setAttribute('aria-hidden', 'true');
    spacer.dataset.spacer = 'trailing';
    spacer.dataset.page = String(page);
    return spacer;
  }

  /**
   * Column count of the grid layout, or 0 when spacers and placeholders must
   * not be used: the `organic` layout places cards by nth-of-type spans that
   * an extra cell would throw off (and it is not the layout the collection
   * grid ships with).
   *
   * @returns {number}
   */
  #columnCount() {
    const { grid } = this.refs;
    if (!grid || !grid.classList.contains('product-grid--grid')) return 0;
    return getComputedStyle(grid).gridTemplateColumns.split(' ').length;
  }

  /**
   * @returns {HTMLElement[]}
   */
  #cards() {
    const { grid } = this.refs;
    if (!grid) return [];
    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > [ref="cards[]"]')]);
  }

  /**
   * @param {'top' | 'trailing' | 'row'} [kind]
   * @returns {HTMLElement[]}
   */
  #spacers(kind) {
    const { grid } = this.refs;
    if (!grid) return [];
    const selector = kind ? `:scope > [data-spacer="${kind}"]` : ':scope > [data-spacer]';
    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(selector)]);
  }

  /**
   * One cell per collapsed page: the full-width placeholder, or the first row
   * spacer of a page that has no whole row.
   * @returns {HTMLElement[]}
   */
  #placeholders() {
    const { grid } = this.refs;
    if (!grid) return [];
    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(':scope > [data-placeholder-main]')]);
  }

  /**
   * Everything standing in for a collapsed page: its row spacers and its
   * placeholder, in DOM order.
   * @param {number} page
   * @returns {HTMLElement[]}
   */
  #placeholderCells(page) {
    const { grid } = this.refs;
    if (!grid) return [];
    return /** @type {HTMLElement[]} */ ([...grid.querySelectorAll(`:scope > [data-placeholder-page="${page}"]`)]);
  }

  /**
   * The spacer cells that bring `inserting` cards, standing where `replacing`
   * cells were, back to the same column offset for everything that follows.
   *
   * With `t` cells removed and `m` cards added, later cards move by `m - t`
   * columns; `q` extra cells with `m + q ≡ t (mod columns)` undo that.
   *
   * Where the padding goes decides whether anyone sees it. `top` spacers lead
   * the page: they sit in the first row of the topmost page in the DOM, which
   * the next prepend replaces before the shopper gets there, so the grid they
   * scroll through stays continuous. Page 1 has no page before it to hide them
   * behind, so its padding trails instead: one short last row at the page 1 /
   * page 2 seam, which reads as a page break. Trailing spacers carry the page
   * they follow so they collapse and restore with it; `top` spacers belong to
   * whatever is above the grid's first page.
   *
   * @param {number} inserting
   * @param {number} replacing
   * @param {number | undefined} page - The page being inserted.
   * @returns {HTMLElement[]}
   */
  #spacersToPad(inserting, replacing, page) {
    const columns = this.#columnCount();
    if (columns < 2) return [];

    const count = (((replacing - inserting) % columns) + columns) % columns;
    const trailing = page === 1;
    return Array.from({ length: count }, () => {
      const spacer = document.createElement('li');
      spacer.className = `product-grid__item ${PaginatedList.spacerClass}`;
      spacer.setAttribute('aria-hidden', 'true');
      spacer.dataset.spacer = trailing ? 'trailing' : 'top';
      if (trailing) spacer.dataset.page = String(page);
      return spacer;
    });
  }

  /**
   * Spacers, placeholders and remembered card heights are all computed for a
   * column count. When it changes — a resize, the mobile-single or zoom-out
   * view — every row re-flows regardless, so the old padding can only add
   * stray holes, the old heights can only mis-size rows and a placeholder's
   * height means nothing; drop them all and let the grid lay out afresh. The
   * collapsed pages are reached again through the top sentinel, from cache.
   */
  #syncToColumns() {
    const columns = this.#columnCount();
    if (columns === this.#columns) return;
    this.#columns = columns;
    for (const spacer of this.#spacers()) spacer.remove();
    for (const placeholder of this.#placeholders()) {
      this.#placeholderObserver?.unobserve(placeholder);
      placeholder.remove();
    }
    this.#cardHeights.clear();
    this.#trailingSpacers.clear();
    for (const [page, cards] of this.#collapsedCards) {
      if (!this.pages.has(page)) this.pages.set(page, `<div ref="grid">${cards.map((card) => card.outerHTML).join('')}</div>`);
    }
    this.#collapsedCards.clear();
    for (const card of this.#cards()) card.style.removeProperty('contain-intrinsic-block-size');
  }

  /**
   * The card to hold still when cards are inserted above the viewport: the
   * topmost one the shopper can see, or the grid's first card when none is on
   * screen (the grid is below the fold, so nothing above it can move anyway).
   *
   * It has to be a card IN VIEW, not the first card the grid keeps: rows
   * between a far-off anchor and the viewport could absorb layout changes of
   * their own, and anchoring on the visible card measures exactly the shift
   * the shopper would see.
   *
   * Never `firstElementChild`: the grid also holds a non-rendered <script>, and
   * a zero-height anchor would silently make the compensation a no-op.
   *
   * @returns {HTMLElement | null}
   */
  #viewportAnchor() {
    /** @type {HTMLElement | null} */
    let first = null;
    for (const card of this.#cards()) {
      first ??= card;
      const { top, bottom } = card.getBoundingClientRect();
      if (bottom > 0 && top < window.innerHeight) return card;
    }
    return first;
  }

  /**
   * Whether the grid already contains cards for the given page.
   *
   * @param {number} page
   * @returns {boolean}
   */
  #isPageRendered(page) {
    return Boolean(this.refs.grid?.querySelector(`:scope > [ref="cards[]"][data-page="${page}"]`));
  }

  /**
   * Whether the grid holds the given page in any form — rendered, or
   * collapsed into its placeholder. Final guard against inserting a page
   * twice (the background prepend, the sentinel observer and the initial
   * markup can all overlap on the same page).
   *
   * @param {number} page
   * @returns {boolean}
   */
  #isPageInDom(page) {
    return this.#isPageRendered(page) || Boolean(this.refs.grid?.querySelector(`:scope > [data-placeholder-page="${page}"]`));
  }

  /**
   * Every page the grid holds, top to bottom, and whether it is rendered or
   * collapsed.
   *
   * @returns {{ page: number, rendered: boolean }[]}
   */
  #pagesInDom() {
    const { grid } = this.refs;
    if (!grid) return [];
    /** @type {Map<number, boolean>} */
    const pages = new Map();
    for (const child of grid.children) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.getAttribute('ref') === 'cards[]') {
        const page = Number(child.dataset.page);
        if (Number.isFinite(page)) pages.set(page, true);
      } else if (child.dataset.placeholderMain !== undefined) {
        const page = Number(child.dataset.placeholderPage);
        if (Number.isFinite(page) && !pages.has(page)) pages.set(page, false);
      }
    }
    return [...pages.entries()].map(([page, rendered]) => ({ page, rendered })).sort((a, b) => a.page - b.page);
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';
    const inDom = this.#pagesInDom();
    const edge = inDom[isPrevious ? 0 : inDom.length - 1];

    // Normally the cursor is the page at the edge of the grid. A page whose
    // products were all skipped renders no cards at all, so there is none to
    // read: fall back to the page the server was asked for. Without this,
    // landing on such a page is terminal in both directions — nothing ever
    // loads and the shopper is left on a blank grid.
    const currentCardPage = edge ? edge.page : Number(new URL(window.location.href).searchParams.get('page') ?? 1);

    if (!Number.isFinite(currentCardPage)) return;

    let page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    // Step over pages already known to render nothing — see #emptyPages.
    while (this.#emptyPages.has(page)) page += isPrevious ? -1 : 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * The cards of a page as fresh elements, from the client sort's ordered set
   * or the cached section HTML.
   *
   * @param {number} page
   * @returns {Element[] | undefined}
   */
  #getGridForPage(page) {
    if (this.#clientSort) {
      const cards = this.#clientSort.pages?.get(page);
      return cards ? cards.map((card) => /** @type {Element} */ (card.cloneNode(true))) : undefined;
    }

    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return [...gridElement.querySelectorAll(':scope > [ref="cards[]"]')];
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * The client sort the current URL asks for, if any.
   * @returns {string | null}
   */
  static #clientSortKey() {
    const sort = new URL(window.location.href).searchParams.get('sort_by');
    return sort && sort in PaginatedList.clientSorts ? sort : null;
  }

  /**
   * Loads every page of the results, orders the cards by the sort's attribute
   * and pages through the ordered set from then on. While it loads, the
   * server-ordered grid stays up, dimmed and marked busy (see base.css); the
   * infinite scroll waits, so the shopper is never handed pages in an order
   * that is about to change under them.
   *
   * @param {string} key
   */
  async #startClientSort(key) {
    const { grid } = this.refs;
    const sort = PaginatedList.clientSorts[key];
    if (!grid || !sort) return;

    const generation = (this.#clientSort?.generation ?? 0) + 1;
    this.#clientSort = { key, pages: null, generation };
    const stale = () => !this.isConnected || this.#clientSort?.generation !== generation;

    const lastPage = Number(grid.dataset.lastPage) || 1;
    const status = this.#sortStatus(sort.label, 0, lastPage);
    this.setAttribute('client-sorting', '');

    const perPage = this.#cards().length || 24;
    const url = new URL(window.location.href);
    url.hash = '';

    /** @type {Map<number, Element[]>} */
    const fetched = new Map();
    let done = 0;
    const load = async (page) => {
      let html = this.pages.get(page);
      for (let attempt = 0; attempt < 2 && !html?.includes('ref="grid"'); attempt++) {
        const pageUrl = new URL(url);
        pageUrl.searchParams.set('page', String(page));
        try {
          html = await sectionRenderer.getSectionHTML(this.sectionId, attempt === 0, pageUrl);
        } catch {
          html = undefined;
        }
      }
      if (html) this.pages.set(page, html);
      const parsed = html ? new DOMParser().parseFromString(html, 'text/html') : null;
      fetched.set(page, parsed ? [...parsed.querySelectorAll('[ref="grid"] > [ref="cards[]"]')] : []);
      done++;
      if (!stale()) this.#sortStatus(sort.label, done, lastPage, status);
    };

    const queue = Array.from({ length: lastPage }, (_, i) => i + 1);
    await Promise.all(
      Array.from({ length: PaginatedList.clientSortConcurrency }, async () => {
        while (queue.length && !stale()) await load(/** @type {number} */ (queue.shift()));
      })
    );
    if (stale()) return;

    // Stable: equal scores keep the storefront's order.
    const cards = [];
    for (let page = 1; page <= lastPage; page++) cards.push(...(fetched.get(page) ?? []));
    const score = (card) => {
      const value = parseFloat(card.getAttribute(sort.attribute) ?? card.querySelector(`[${sort.attribute}]`)?.getAttribute(sort.attribute) ?? '');
      return Number.isFinite(value) ? value : -Infinity;
    };
    const seen = new Set();
    const unique = cards.filter((card) => {
      const id = card.getAttribute('data-product-id') || card.id;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    unique.sort((a, b) => (sort.descending ? score(b) - score(a) : score(a) - score(b)));

    /** @type {Map<number, Element[]>} */
    const pages = new Map();
    unique.forEach((card, index) => {
      const page = Math.floor(index / perPage) + 1;
      if (card instanceof HTMLElement) card.dataset.page = String(page);
      const list = pages.get(page) ?? [];
      list.push(card);
      pages.set(page, list);
    });
    this.#clientSort.pages = pages;
    this.#emptyPages.clear();

    // Show the page the URL names — the one the shopper left for a PDP, say.
    const requested = Number(new URL(window.location.href).searchParams.get('page') ?? 1);
    const page = pages.has(requested) ? requested : 1;
    grid.dataset.lastPage = String(Math.max(pages.size, 1));
    this.#epoch++;
    for (const cell of [...this.#cards(), ...this.#spacers(), ...this.#placeholders()]) cell.remove();
    this.#visibleCards.clear();
    this.#cardHeights.clear();
    this.#trailingSpacers.clear();
    this.#collapsedCards.clear();
    const elements = this.#getGridForPage(page) ?? [];
    grid.append(...elements);
    this.updatedCallback();
    this.#aspectRatioHelper?.processNewElements();

    status.remove();
    this.removeAttribute('client-sorting');

    const hash = window.location.hash.slice(1);
    const target = (hash && document.getElementById(hash)?.closest('.product-grid__item')) || (page > 1 ? elements[0] : null);
    if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });

    this.#start();
  }

  /**
   * The line under the sort control that says the results are being ordered.
   *
   * @param {string} label
   * @param {number} done
   * @param {number} total
   * @param {HTMLElement} [element]
   * @returns {HTMLElement}
   */
  #sortStatus(label, done, total, element) {
    const status = element ?? this.querySelector('.product-grid__sort-status') ?? document.createElement('p');
    if (!status.isConnected) {
      status.className = 'product-grid__sort-status';
      status.setAttribute('role', 'status');
      this.refs.grid?.before(status);
    }
    status.textContent = total > 1 ? `Sorting by ${label}… ${done}/${total}` : `Sorting by ${label}…`;
    return status;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#epoch++;
    this.#filtering = true;

    // Page numbers mean something different under a new filter, so which of
    // them render nothing has to be learned again from scratch.
    this.#emptyPages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // A client sort in progress is for the old results: cancel it.
    if (this.#clientSort) {
      this.#clientSort = { ...this.#clientSort, pages: null, generation: this.#clientSort.generation + 1 };
    }

    // The new grid arrives with the section morph. Three things can announce
    // it, and the first one wins: a mutation of the grid's children; the
    // facets form's `filter:rendered`, which fires even when the morph found
    // nothing to change (switching between two sorts the server renders in
    // the same order — the default and a client sort, say); and a timeout,
    // in case neither ever comes.
    const { grid } = this.refs;
    /** @type {MutationObserver | undefined} */
    let observer;
    /** @type {ReturnType<typeof setTimeout>} */
    let fallback;
    const finish = () => {
      observer?.disconnect();
      clearTimeout(fallback);
      document.removeEventListener(ThemeEvents.FilterRendered, finish);
      if (!this.#filtering) return;
      this.#filtering = false;
      if (this.isConnected) this.#restartAfterFilter();
    };
    document.addEventListener(ThemeEvents.FilterRendered, finish);
    if (grid) {
      observer = new MutationObserver(finish);
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });
    }
    fallback = setTimeout(finish, 3000);
  };

  /**
   * Rebuilds the list around the grid a filter or sort change has just
   * rendered.
   */
  #restartAfterFilter() {
    // The grid's cards were replaced — the window state described the old
    // ones. Any placeholder or spacer the morph left behind goes with them.
    this.#visibleCards.clear();
    this.#cardHeights.clear();
    this.#trailingSpacers.clear();
    this.#collapsedCards.clear();
    for (const cell of [...this.#spacers(), ...this.#placeholders()]) cell.remove();

    // A different result set, and the morph reset the grid's inline
    // style, so the measured estimate went with it. Re-attach in case the
    // grid element itself was swapped rather than morphed in place.
    this.#observeCardEstimate();
    this.#scheduleEstimate();

    const sortKey = PaginatedList.#clientSortKey();
    if (sortKey) {
      this.#startClientSort(sortKey);
      return;
    }
    this.#clientSort = null;
    this.removeAttribute('client-sorting');
    this.querySelector('.product-grid__sort-status')?.remove();
    this.#start();
  }
}
